import { Logger } from "./logger";

const _e = 0.09;
const _gameloopInterval = 16.67; //in milliseconds, 60 times a second
let gameloop: any | null = null;
let gamepadsPreviousButtonsStates: Record<number, Record<number, { value: number, pressed: boolean }>> = {};
let gamepadsPreviousAxesStates: Record<number, number[]> = {};
let gamepadsConnectedTimeStamp: Record<number, number | string> = {};
const _axisOffset = 100;
const _axisMultiplier = 1;
const _axisYInverted = -1;

export interface GamepadButtonEventInit extends EventInit {
  index: number;
  id: number | string;
  value: number;
}

export class GamepadButtonEvent extends Event {
  public index: number;
  public id: number | string;
  public value: number;
    button: any;
    pressed!: boolean;

  constructor(type: string, eventInitDict: GamepadButtonEventInit) {
    super(type, eventInitDict);
    this.index = eventInitDict.index;
    this.id = eventInitDict.id;
    this.value = eventInitDict.value;
  }
}

export interface GamepadAxisEventInit extends EventInit {
  index: number;
  x: number;
  y: number;
  id: number | string;
}

export class GamepadAxisEvent extends Event {
  public index: number;
  public x: number;
  public y: number;
  public id: number | string;
  button: any;
  pressed!: boolean;

  constructor(type: string, eventInitDict: GamepadAxisEventInit) {
    super(type, eventInitDict);
    this.index = eventInitDict.index;
    this.x = eventInitDict.x;
    this.y = eventInitDict.y;
    this.id = eventInitDict.id;
  }
}

export function storePreviousState(gamepad: Gamepad): void {
  gamepadsPreviousButtonsStates[gamepad.index] = {};
  gamepad.buttons.forEach((button, index) => {
    gamepadsPreviousButtonsStates[gamepad.index][index] = {
      value: button.value,
      pressed: button.pressed,
    };
  });

  gamepadsPreviousAxesStates[gamepad.index] = Array(gamepad.axes.length);
  for (let index = 0; index < gamepad.axes.length; index++) {
    gamepadsPreviousAxesStates[gamepad.index][index] = gamepad.axes[index];
  }
}

export function checkAxes(gamepad: Gamepad, previousGamePad: number[]): void {
  for (let i = 0; i < gamepad.axes.length; i += 2) {
    const absX = Math.abs(gamepad.axes[i]);
    const absY = Math.abs(gamepad.axes[i + 1]);
    let event: GamepadAxisEvent | null = null;
    if (absX > _e || absY > _e) {
      event = new GamepadAxisEvent("gamepadAxis", {
        id: gamepadsConnectedTimeStamp[gamepad.index],
        index: i / 2 + _axisOffset,
        x: gamepad.axes[i] * _axisMultiplier,
        y: gamepad.axes[i + 1] * _axisMultiplier * _axisYInverted,
      });
      document.dispatchEvent(event);
    } else {
      const previousAbsX = Math.abs(previousGamePad[i]);
      const previousAbsY = Math.abs(previousGamePad[i + 1]);

      // have to send if previously was moved
      if (previousAbsX > _e || previousAbsY > _e) {
        event = new GamepadAxisEvent("gamepadAxis", {
          id: gamepadsConnectedTimeStamp[gamepad.index],
          index: i / 2 + _axisOffset,
          x: 0.0,
          y: 0.0,
        });
        document.dispatchEvent(event);
      }
    }
  }
}

export function gameLoop(): void {
  Object.keys(gamepadsPreviousAxesStates).forEach((gamepadIndex) => {
    const index = Number(gamepadIndex);
    const gamepad = navigator.getGamepads()[index];
    if (!gamepad) return;

    const previousButtons = gamepadsPreviousButtonsStates[index];
    gamepad.buttons.forEach((button, buttonIndex) => {
      const buttonStatus = button.value > 0 || button.pressed;
      const previousButtonStatus =
        previousButtons[buttonIndex].value > 0 ||
        previousButtons[buttonIndex].pressed;

      let event: GamepadButtonEvent | undefined;
      if (buttonStatus !== previousButtonStatus) {
        if (buttonStatus) {
          event = new GamepadButtonEvent("gamepadButtonDown", {
            id: gamepadsConnectedTimeStamp[gamepad.index],
            index: buttonIndex,
            value: button.value,
          });
        } else {
          event = new GamepadButtonEvent("gamepadButtonUp", {
            id: gamepadsConnectedTimeStamp[gamepad.index],
            index: buttonIndex,
            value: 0,
          });
        }
        document.dispatchEvent(event);
      } else if (buttonStatus) {
        event = new GamepadButtonEvent("gamepadButtonPressed", {
          id: gamepadsConnectedTimeStamp[gamepad.index],
          index: buttonIndex,
          value: button.value,
        });
        document.dispatchEvent(event);
      }
    });

    checkAxes(gamepad, gamepadsPreviousAxesStates[index]);
    storePreviousState(gamepad);
  });
}

export function getCookie(cname: string): string {
  const name = `${cname}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

export function gamepadHandler(event: GamepadEvent, connecting: boolean): void {
  const gamepad = event.gamepad;

  const key = gamepad.id.replace(/\s/g, "");
  const cookieTimeStamp = getCookie(key);

  if (connecting) {
    storePreviousState(gamepad);
    if (Object.keys(gamepadsPreviousAxesStates).length === 1) {
      gameloop = setInterval(gameLoop, _gameloopInterval);
    }

    // Try to find the timestamp
    if (cookieTimeStamp === "") {
      document.cookie = `${key}=${gamepad.timestamp}`;
      gamepadsConnectedTimeStamp[gamepad.index] = gamepad.timestamp;
    } else {
      gamepadsConnectedTimeStamp[gamepad.index] = cookieTimeStamp;
    }

    Logger.log(`connected: ${gamepadsConnectedTimeStamp[gamepad.index]}`);
  } else {
    delete gamepadsPreviousAxesStates[gamepad.index];
    delete gamepadsPreviousButtonsStates[gamepad.index];
    if (Object.keys(gamepadsPreviousAxesStates).length === 0) {
      clearInterval(gameloop!);
      gameloop = null;
    }
    Logger.log(`disconnected: ${gamepad.id}`);
  }
}
